import { useMemo, useState } from "react";
import { Search, UserPlus, Users, Phone, Star } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { vals } from "@/lib/format";
import { BigButton, Chip, Empty, PageTitle } from "@/components/ui-kit";
import CustomerFormDialog from "@/components/customers/CustomerFormDialog";
import CustomerDetail from "@/components/customers/CustomerDetail";
import DataGrid from "@/components/DataGrid";
import useIsDesktop from "@/lib/useIsDesktop";
import { fmtDate } from "@/lib/format";

export function balanceTone(c) {
  if (!c.balance || c.balance <= 0) return "bg-[#E3F6EC] text-[#1F9D55]";
  if (c.credit_limit > 0 && c.balance > c.credit_limit) return "bg-destructive/10 text-destructive";
  return "bg-[#E67E22]/10 text-[#A85A12]";
}

export default function Customers() {
  const { view, t, money, lang } = useApp();
  const isDesktop = useIsDesktop();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const all = vals(view.customers);
  const totalDebt = all.reduce((s, c) => s + Math.max(c.balance || 0, 0), 0);

  const list = useMemo(() => all
    .filter((c) => `${c.name} ${c.family || ""} ${c.phone || ""}`.toLowerCase().includes(q.toLowerCase()))
    .filter((c) => filter === "all" || (filter === "owing" ? c.balance > 0 : c.credit_limit > 0 && c.balance > c.credit_limit))
    .sort((a, b) => (b.balance || 0) - (a.balance || 0)), [all, q, filter]);

  return (
    <div>
      <PageTitle testid="customers-title" action={<BigButton onClick={() => setAdding(true)} data-testid="customer-add-btn"><UserPlus className="w-5 h-5" /><span className="hidden sm:inline">{t("add_customer")}</span></BigButton>}>
        {t("nav_customers")}
      </PageTitle>
      <div className="ak-strip rounded-xl bg-[#F0F0F0] text-[#333333] p-5 mb-4 flex justify-between items-center">
        <span className="font-bold">{t("total_debt")}</span>
        <span className="text-2xl font-extrabold text-[#F39C12]" data-testid="customers-total-debt">{money(totalDebt)}</span>
      </div>
      <div className="relative mb-3">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-5 h-5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_customers")} data-testid="customers-search-input"
          className="w-full h-14 rounded-2xl border border-border bg-white ps-11 pe-3 focus:outline-none focus:ring-2 focus:ring-primary" />
      </div>
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {["all", "owing", "over_limit"].map((k) => <Chip key={k} active={filter === k} onClick={() => setFilter(k)} data-testid={`customers-filter-${k}`}>{t(`filter_${k}`)}</Chip>)}
      </div>
      {isDesktop ? (
        <DataGrid testid="customers-grid" name="customers" rows={list} onRowClick={(c) => setOpenId(c.id)} onAdd={() => setAdding(true)} dateField="last_purchase_at"
          columns={[
            { key: "name", label: t("name") }, { key: "family", label: t("family") }, { key: "phone", label: t("phone") },
            { key: "credit_limit", label: t("credit_limit"), align: "end" }, { key: "points", label: t("points"), align: "end" },
            { key: "last_purchase_at", label: t("last_purchase"), get: (c) => (c.last_purchase_at ? fmtDate(c.last_purchase_at, lang) : "") },
            { key: "balance", label: t("balance"), align: "end", sum: true, get: (c) => c.balance || 0, render: (c) => <span className={`px-2 rounded font-bold ${balanceTone(c)}`}>{money(c.balance)}</span> },
          ]} />
      ) : list.length === 0 ? <Empty icon={Users} text={t("no_customers")} testid="customers-empty" /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {list.map((c) => (
            <button key={c.id} type="button" onClick={() => setOpenId(c.id)} data-testid="customer-family-tab-card" data-customer-id={c.id}
              className="rounded-2xl bg-white border border-border p-4 flex items-center gap-3 text-start hover:border-primary transition-colors rise">
              <span className="w-12 h-12 rounded-full bg-secondary grid place-items-center font-heading font-black text-lg shrink-0">{c.name.slice(0, 1)}</span>
              <span className="flex-1 min-w-0">
                <span className="block font-bold text-lg truncate">{c.name}</span>
                <span className="block text-sm text-muted-foreground truncate">{c.family}{c.phone && <><Phone className="inline w-3 h-3 mx-1" /><span dir="ltr">{c.phone}</span></>}</span>
                {c.points > 0 && <span className="text-xs font-bold text-[#F39C12] flex items-center gap-1"><Star className="w-3 h-3" />{c.points} {t("points")}</span>}
              </span>
              <span className={`px-3 py-1.5 rounded-lg font-extrabold text-sm shrink-0 ${balanceTone(c)}`} data-testid="customer-card-balance">{money(c.balance)}</span>
            </button>
          ))}
        </div>
      )}
      <CustomerFormDialog open={adding} onOpenChange={setAdding} />
      <CustomerDetail customerId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
